import { Dayjs } from 'dayjs'
import { WritableDraft } from 'immer'
import { ReactNode } from 'react'

import { Schemas } from '~/clients/api-client'

import { Location } from '../selectors'
import { StoreState } from '../store'
import type { OperationalPlannerFilterType } from './operationalPlannerFilterSlice'
import type { SurgeonSchedulerFilterType } from './surgeonSchedulerFilterSlice'
import type { SurgerySchedulerFilterType } from './surgerySchedulerFilterSlice'
import type { WaitingListFilterType } from './waitingListFilterSlice'

export type VisibilityOptionLevel = 'page' | 'item'

export type VisibilityOption = {
    value: boolean
    label: string
    icon: ReactNode
    level: VisibilityOptionLevel
}

export type LayoutOptions<Page extends string> = Record<Page, string>
export type VisibilityOptions<T extends string> = Record<T, VisibilityOption>

export type PageConfigSliceState<TVisibilityOptions extends string, TPages extends string> = {
    selectedLayouts?: LayoutOptions<TPages>
    visibilityOptions: VisibilityOptions<TVisibilityOptions>
}

export type LocationDay = {
    date: Dayjs
    locationId: Location['id']
}

export type DrawerState<TTabValue extends string = never> = {
    isDrawerOpen: boolean
} & ([TTabValue] extends [never] ? object : { activeDrawerTab: TTabValue })

export type FilterOption = {
    label: string
    value: string
}

type OptionalIfUndefined<T> = [T] extends [undefined] ? () => void : undefined extends T ? (args?: T) => void : (args: T) => void

export type DrawerActions<TTabValue extends string = never, TOpenDrawerArgs extends Record<string, unknown> | undefined = undefined> = {
    openDrawer: OptionalIfUndefined<TOpenDrawerArgs>
    closeDrawer: () => void
    toggleDrawer: () => void
} & ([TTabValue] extends [never] ? object : { setActiveDrawerTab: (tabValue: TTabValue) => void })

export type FilterSliceKey = keyof Pick<StoreState, 'operationalPlannerFilters' | 'waitingListFilters' | 'surgerySchedulerFilters' | 'surgeonSchedulerFilters'>

/**
 * Union type of all filter types across operational planner, waiting list, surgery scheduler, and surgeon scheduler slices.
 * Used for filter profiles that need to handle multiple page types.
 */
export type PageFilterType = OperationalPlannerFilterType | WaitingListFilterType | SurgerySchedulerFilterType | SurgeonSchedulerFilterType

export type BaseFilter = {
    type: string
    value: string
    label: string
    id: string
}

export type FilterSliceActions<TFilterType extends string> = {
    setProfile: (profile: Schemas['ViewOptionProfileOut'] | undefined) => void
    emptyFilters: () => void
    removeSingleFilter: (id: string) => void
    toggleFilteredBy: (args: { value: string; label: string; type: TFilterType }) => void
    toggleFilteredBySelection: (args: { filters: { value: string; label: string }[]; type: TFilterType }) => void
    removeAllFilterByType: (type: TFilterType) => void
}

export function createFilterSliceActions<TFilterType extends string, TFilter extends BaseFilter>(
    sliceName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    set: (fn: (state: WritableDraft<any>) => void) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get: () => any
): FilterSliceActions<TFilterType> {
    return {
        emptyFilters: () =>
            set(state => {
                state[sliceName].filters = []
            }),
        removeSingleFilter: id =>
            set(state => {
                state[sliceName].filters = state[sliceName].filters.filter((filter: TFilter) => filter.id !== id)
            }),
        toggleFilteredBy: ({ value, label, type }) => {
            const existingFilter = get()[sliceName].filters.find((filter: TFilter) => filter.type === type && filter.value === value)

            if (existingFilter) {
                set(state => {
                    state[sliceName].filters = state[sliceName].filters.filter((filter: TFilter) => filter.type !== type || filter.value !== value)
                })
            } else {
                set(state => {
                    state[sliceName].filters.push({
                        id: `${type}-${label}-${value}`,
                        type,
                        value,
                        label,
                    })
                })
            }
        },
        toggleFilteredBySelection: ({ filters, type }) => {
            set(state => {
                const currentFilters = state[sliceName].filters
                const otherFilters = currentFilters.filter((filter: TFilter) => filter.type !== type)
                const newFilters = filters.map(({ value, label }) => ({
                    id: `${type}-${value}-${value}`,
                    type,
                    value,
                    label,
                }))
                state[sliceName].filters = [...otherFilters, ...newFilters]
            })
        },
        removeAllFilterByType: type =>
            set(state => {
                state[sliceName].filters = state[sliceName].filters.filter((filter: TFilter) => filter.type !== type)
            }),
        setProfile: (profile: Schemas['ViewOptionProfileOut'] | undefined) => {
            set(state => {
                if (profile?.filter_profile) {
                    state[sliceName].filters = Object.entries(profile.filter_profile).flatMap(([type, filterValues]) =>
                        (filterValues as string[]).map(value => ({
                            id: `${type}-${value}-${value}`,
                            type: type as TFilterType,
                            value: value,
                            label: value,
                        }))
                    )
                }
            })
        },
    }
}
